import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Calendar, Clock, Check, Repeat, CreditCard,
  Shield, ChevronDown, Loader2, Star, AlertTriangle, X, Pause, Ban,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { DarkModeToggle } from "../components/DarkModeToggle";

const API = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

export default function RecurringBooking() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("browse"); // browse | configure | preview
  const [slots, setSlots] = useState([]);
  const [instructorId, setInstructorId] = useState("");
  const [loading, setLoading] = useState(true);

  // Config
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState(120);
  const [frequency, setFrequency] = useState("weekly");
  const [numSessions, setNumSessions] = useState(0); // 0 = ongoing
  const [startDate, setStartDate] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("per_lesson"); // per_lesson or pay_all_now

  // My series
  const [mySeries, setMySeries] = useState([]);
  const [showMySeries, setShowMySeries] = useState(false);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setStartDate(tomorrow.toISOString().split("T")[0]);
  }, []);

  const fetchSlots = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/recurring/available`, { withCredentials: true });
      setSlots(data.slots || []);
      setInstructorId(data.instructor_id || "");
    } catch {}
    setLoading(false);
  }, []);

  const fetchMySeries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/recurring/my-series`, { withCredentials: true });
      setMySeries(data.series || []);
    } catch {}
  }, []);

  useEffect(() => { fetchSlots(); fetchMySeries(); }, [fetchSlots, fetchMySeries]);

  const durLabel = (d) => d === 60 ? "1 Hour" : d === 90 ? "90 Min" : d === 120 ? "2 Hours" : `${d}min`;
  const priceFor = (d) => d === 60 ? 45 : d === 90 ? 67 : d === 120 ? 85 : Math.round(d * (85 / 120));

  const handleBook = async () => {
    if (!selectedSlot || !policyAccepted) return;
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/recurring/book`, {
        instructor_id: instructorId,
        weekday: selectedSlot.weekday,
        start_time: selectedSlot.start_time,
        duration,
        frequency,
        num_sessions: numSessions,
        start_date: startDate,
        origin_url: window.location.origin,
        policy_accepted: true,
        payment_mode: paymentMode,
      }, { withCredentials: true });

      if (data.founder_mode || data.no_charge) {
        toast.message(data.message || "Admin billing bypass — use a real test account.", { duration: 6000 });
        setCheckoutLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setCheckoutLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
      setCheckoutLoading(false);
    }
  };

  const cancelSeries = async (seriesId) => {
    if (!window.confirm("Cancel this recurring series? All future unpaid lessons will be cancelled.")) return;
    try {
      const { data } = await axios.post(`${API}/api/recurring/${seriesId}/cancel`, {}, { withCredentials: true });
      toast.success(`Series cancelled. ${data.lessons_cancelled} future lessons removed.`);
      fetchMySeries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const pauseWeek = async (seriesId, date) => {
    try {
      await axios.post(`${API}/api/recurring/${seriesId}/pause-week`, { date }, { withCredentials: true });
      toast.success(`Lesson on ${date} paused`);
      fetchMySeries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120]">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button data-testid="recurring-back" onClick={() => step === "browse" ? nav("/learn/book") : setStep("browse")} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">Recurring Lessons</h1>
            <p className="text-xs text-slate-400">Book your regular weekly slot</p>
          </div>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">

        {/* My Active Series */}
        {mySeries.length > 0 && (
          <div>
            <button data-testid="toggle-my-series" onClick={() => setShowMySeries(!showMySeries)}
              className="w-full flex items-center justify-between bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-bold text-slate-900 dark:text-white">My Recurring Lessons ({mySeries.filter(s => s.status === "active").length})</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showMySeries ? "rotate-180" : ""}`} />
            </button>
            {showMySeries && (
              <div className="mt-2 space-y-2">
                {mySeries.map(s => (
                  <div key={s.id} data-testid={`series-${s.id}`} className={`bg-white dark:bg-slate-800 rounded-2xl border p-4 ${s.status === "active" ? "border-blue-200 dark:border-blue-800" : "border-slate-200 dark:border-slate-700 opacity-60"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-base font-bold text-slate-900 dark:text-white">{s.day_name}s at {fmtTime(s.start_time)}</p>
                        <p className="text-xs text-slate-400">{durLabel(s.duration)} — £{s.price_per_lesson} — {s.frequency}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        s.status === "active" ? "bg-emerald-100 text-emerald-700" :
                        s.status === "suspended" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>{s.status}</span>
                    </div>
                    {s.status === "active" && (
                      <div className="flex gap-2 mt-2">
                        {s.next_date && (
                          <button data-testid={`pause-${s.id}`} onClick={() => pauseWeek(s.id, s.next_date)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 text-amber-600 text-xs font-bold">
                            <Pause className="w-3 h-3" />Skip Next
                          </button>
                        )}
                        <button data-testid={`cancel-series-${s.id}`} onClick={() => cancelSeries(s.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 text-red-500 text-xs font-bold">
                          <Ban className="w-3 h-3" />Cancel Series
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Available Slots */}
        {step === "browse" && (
          <>
            <div>
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white mb-3">Available Weekly Slots</h2>
              {loading ? (
                <div className="text-center py-8"><Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto" /></div>
              ) : slots.length === 0 ? (
                <div className="text-center py-8 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
                  <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-bold text-slate-400">No recurring slots available yet</p>
                  <p className="text-xs text-slate-300 mt-1">Your instructor hasn't set up recurring availability</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {slots.map(slot => (
                    <button key={slot.id} data-testid={`slot-${slot.id}`}
                      disabled={!slot.available}
                      onClick={() => {
                        setSelectedSlot(slot);
                        if (slot.durations_allowed?.length === 1) setDuration(slot.durations_allowed[0]);
                        setStep("configure");
                      }}
                      className={`w-full text-left rounded-2xl border p-4 transition-all active:scale-[0.98]
                        ${slot.available
                          ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300"
                          : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-50 cursor-not-allowed"
                        }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-base font-bold text-slate-900 dark:text-white">{slot.day_name}</p>
                          <p className="text-sm text-slate-500">{fmtTime(slot.start_time)}</p>
                        </div>
                        {slot.available ? (
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/15 px-2.5 py-1 rounded-full">Available</span>
                        ) : (
                          <span className="text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-700 px-2.5 py-1 rounded-full">Taken</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Configure */}
        {step === "configure" && selectedSlot && (
          <div className="space-y-5">
            <div className="bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 text-center">
              <p className="text-xs font-bold text-blue-500 uppercase tracking-wider">Your Regular Slot</p>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">{selectedSlot.day_name}s at {fmtTime(selectedSlot.start_time)}</p>
            </div>

            {/* Duration */}
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Lesson Duration</p>
              <div className="grid grid-cols-3 gap-2">
                {(selectedSlot.durations_allowed || [60, 90, 120]).map(d => (
                  <button key={d} data-testid={`dur-${d}`} onClick={() => setDuration(d)}
                    className={`py-3 rounded-xl text-center border-2 transition-all ${
                      duration === d
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-slate-200 dark:border-slate-700"
                    }`}>
                    <p className={`text-sm font-bold ${duration === d ? "text-blue-600" : "text-slate-700 dark:text-slate-300"}`}>{durLabel(d)}</p>
                    <p className={`text-xs font-bold mt-0.5 ${duration === d ? "text-blue-500" : "text-slate-400"}`}>£{priceFor(d)}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Frequency</p>
              <div className="grid grid-cols-2 gap-2">
                {[{ key: "weekly", label: "Every Week" }, { key: "fortnightly", label: "Every 2 Weeks" }].map(f => (
                  <button key={f.key} data-testid={`freq-${f.key}`} onClick={() => setFrequency(f.key)}
                    className={`py-3 rounded-xl text-center border-2 transition-all ${
                      frequency === f.key ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"
                    }`}>
                    <p className={`text-sm font-bold ${frequency === f.key ? "text-blue-600" : "text-slate-700 dark:text-slate-300"}`}>{f.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Sessions */}
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Number of Sessions</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="sessions-ongoing" onClick={() => setNumSessions(0)}
                  className={`py-3 rounded-xl text-center border-2 ${numSessions === 0 ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                  <p className={`text-sm font-bold ${numSessions === 0 ? "text-blue-600" : "text-slate-700 dark:text-slate-300"}`}>Ongoing</p>
                  <p className="text-[10px] text-slate-400">Until cancelled</p>
                </button>
                {[4, 8, 12].map(n => (
                  <button key={n} data-testid={`sessions-${n}`} onClick={() => setNumSessions(n)}
                    className={`py-3 rounded-xl text-center border-2 ${numSessions === n ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                    <p className={`text-sm font-bold ${numSessions === n ? "text-blue-600" : "text-slate-700 dark:text-slate-300"}`}>{n} Lessons</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Start Date */}
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Start From</p>
              <input data-testid="start-date" type="date" value={startDate}
                onChange={e => setStartDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 text-base font-medium bg-white dark:bg-slate-700" />
            </div>

            {/* Summary */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Summary</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">
                {selectedSlot.day_name}s at {fmtTime(selectedSlot.start_time)} — {durLabel(duration)}
              </p>
              <p className="text-sm text-slate-500">
                {frequency === "weekly" ? "Every week" : "Every 2 weeks"} — {numSessions > 0 ? `${numSessions} sessions` : "Ongoing"}
              </p>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                <CreditCard className="w-4 h-4 text-emerald-500" />
                <p className="text-base font-bold text-emerald-600">£{priceFor(duration)} per lesson</p>
              </div>
              <p className="text-xs text-slate-400">First lesson paid now. Future lessons charged 72 hours before.</p>
            </div>

            {/* Payment Mode — only for fixed packages */}
            {numSessions > 0 && (
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Payment Method</p>
                <div className="space-y-2">
                  <button data-testid="pay-per-lesson" onClick={() => setPaymentMode("per_lesson")}
                    className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                      paymentMode === "per_lesson" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700"
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-bold ${paymentMode === "per_lesson" ? "text-blue-600" : "text-slate-700 dark:text-slate-300"}`}>Pay Per Lesson</p>
                        <p className="text-xs text-slate-400 mt-0.5">£{priceFor(duration)} now, then £{priceFor(duration)} before each lesson</p>
                      </div>
                      {paymentMode === "per_lesson" && <Check className="w-5 h-5 text-blue-500" />}
                    </div>
                  </button>
                  <button data-testid="pay-all-now" onClick={() => setPaymentMode("pay_all_now")}
                    className={`w-full text-left rounded-2xl border-2 p-4 transition-all relative overflow-hidden ${
                      paymentMode === "pay_all_now" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/15" : "border-slate-200 dark:border-slate-700"
                    }`}>
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-bl-xl">POPULAR</div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-bold ${paymentMode === "pay_all_now" ? "text-emerald-700 dark:text-emerald-400" : "text-slate-700 dark:text-slate-300"}`}>Pay All Now</p>
                        <p className="text-xs text-slate-400 mt-0.5">{numSessions} lessons — £{priceFor(duration) * numSessions} total</p>
                        <p className="text-[10px] text-emerald-600 font-bold mt-1">Secure all your slots instantly</p>
                      </div>
                      {paymentMode === "pay_all_now" && <Check className="w-5 h-5 text-emerald-500" />}
                    </div>
                  </button>
                </div>
                {paymentMode !== "pay_all_now" && (
                  <p data-testid="upsell-prompt" className="text-xs text-slate-400 italic text-center mt-2">Most learners choose Pay All Now to secure all their slots</p>
                )}
              </div>
            )}

            {/* Payment Summary */}
            <div data-testid="payment-summary" className="bg-slate-900 dark:bg-slate-800 rounded-2xl p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 font-bold uppercase">You pay today</p>
                  <p className="text-3xl font-extrabold">
                    £{paymentMode === "pay_all_now" && numSessions > 0 ? (priceFor(duration) * numSessions).toFixed(0) : priceFor(duration)}
                  </p>
                </div>
                <div className="text-right">
                  {paymentMode === "pay_all_now" && numSessions > 0 ? (
                    <p className="text-xs text-emerald-400 font-bold">{numSessions} lessons included</p>
                  ) : (
                    <p className="text-xs text-slate-400 font-bold">1st lesson</p>
                  )}
                </div>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-bold text-slate-900 dark:text-white">Cancellation Policy</p>
              </div>
              <ul className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                <li className="flex items-start gap-2"><Check className="w-3 h-3 text-emerald-500 mt-0.5" /><span>Cancel or pause 72+ hours before — no charge</span></li>
                <li className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5" /><span>Within 72 hours — lesson fee may be charged</span></li>
                <li className="flex items-start gap-2"><X className="w-3 h-3 text-red-400 mt-0.5" /><span>Cancel entire series anytime — future unpaid lessons removed</span></li>
              </ul>
            </div>

            {/* Policy Checkbox */}
            <label data-testid="recurring-policy-label" className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5">
                <input data-testid="recurring-policy-checkbox" type="checkbox" checked={policyAccepted}
                  onChange={e => setPolicyAccepted(e.target.checked)} className="sr-only" />
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                  policyAccepted ? "bg-blue-600 border-blue-600" : "border-slate-300 group-hover:border-blue-400"
                }`}>{policyAccepted && <Check className="w-3.5 h-3.5 text-white" />}</div>
              </div>
              <span className="text-xs text-slate-600 dark:text-slate-400">I understand the 72-hour cancellation policy and agree to recurring charges.</span>
            </label>

            {/* Book Button */}
            <Button data-testid="confirm-recurring" onClick={handleBook}
              disabled={!policyAccepted || checkoutLoading}
              className={`w-full h-16 rounded-2xl shadow-lg transition-all ${
                policyAccepted ? "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/25" : "bg-slate-300 text-slate-500 cursor-not-allowed shadow-none"
              }`}>
              {checkoutLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /><span className="font-extrabold">Redirecting to payment...</span></div>
              ) : (
                <div className="text-center">
                  <p className="text-base font-extrabold">{policyAccepted
                    ? (paymentMode === "pay_all_now" && numSessions > 0
                      ? `Pay £${(priceFor(duration) * numSessions).toFixed(0)} & Start Series`
                      : "Pay First Lesson & Start Series")
                    : "Accept Policy to Continue"}</p>
                  <p className={`text-xs mt-0.5 ${policyAccepted ? "text-blue-200" : "text-slate-400"}`}>
                    {paymentMode === "pay_all_now" && numSessions > 0
                      ? `${numSessions} lessons — all paid upfront`
                      : `£${priceFor(duration)} now — then £${priceFor(duration)} per lesson`}
                  </p>
                </div>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
